/**
 * AxLabs Experiments - Titration Curves
 *
 * Simulates acid-base titration with pH calculation.
 * Supports strong acid/base and weak acid/base titrations.
 *
 * Key equations:
 * - Henderson-Hasselbalch: pH = pKa + log([A⁻]/[HA])
 * - Strong acid: pH = -log[H⁺]
 * - Buffer capacity at equivalence points
 */
import { BaseSimulationEngine, DIMENSIONS, } from '@axlabs/simulation-core';
// ============================================================================
// PARAMETERS
// ============================================================================
const PARAMETERS = [
    {
        id: 'analyteType',
        name: 'Analyte Type',
        symbol: '',
        unit: '',
        dimension: DIMENSIONS.DIMENSIONLESS,
        min: 1,
        max: 4,
        default: 1,
        step: 1,
        description: '1=Strong Acid, 2=Weak Acid, 3=Strong Base, 4=Weak Base',
    },
    {
        id: 'analyteConc',
        name: 'Analyte Concentration',
        symbol: 'C_a',
        unit: 'M',
        dimension: DIMENSIONS.CONCENTRATION,
        min: 0.01,
        max: 2,
        default: 0.1,
        step: 0.01,
        description: 'Initial concentration of analyte',
    },
    {
        id: 'analyteVolume',
        name: 'Analyte Volume',
        symbol: 'V_a',
        unit: 'mL',
        dimension: DIMENSIONS.LENGTH, // Using LENGTH as proxy for volume
        min: 10,
        max: 100,
        default: 50,
        step: 5,
        description: 'Initial volume of analyte solution',
    },
    {
        id: 'titrantConc',
        name: 'Titrant Concentration',
        symbol: 'C_t',
        unit: 'M',
        dimension: DIMENSIONS.CONCENTRATION,
        min: 0.01,
        max: 2,
        default: 0.1,
        step: 0.01,
        description: 'Concentration of titrant (base for acid, acid for base)',
    },
    {
        id: 'pKa',
        name: 'pKa (for weak acid/base)',
        symbol: 'pK_a',
        unit: '',
        dimension: DIMENSIONS.DIMENSIONLESS,
        min: 1,
        max: 13,
        default: 4.76,
        step: 0.1,
        description: 'Acid dissociation constant (acetic acid = 4.76)',
    },
    {
        id: 'additionRate',
        name: 'Titrant Addition Rate',
        symbol: '',
        unit: 'mL/s',
        dimension: DIMENSIONS.VELOCITY,
        min: 0.1,
        max: 5,
        default: 0.5,
        step: 0.1,
        description: 'Rate of titrant addition',
    },
];
// ============================================================================
// EQUATIONS
// ============================================================================
const EQUATIONS = [
    {
        id: 'strong_acid_ph',
        name: 'Strong Acid pH',
        latex: 'pH = -\\log[H^+]',
        variables: ['pH'],
        description: 'pH from hydrogen ion concentration',
        condition: (params) => (params['analyteType'] ?? 1) === 1,
    },
    {
        id: 'henderson_hasselbalch',
        name: 'Henderson-Hasselbalch',
        latex: 'pH = pK_a + \\log\\frac{[A^-]}{[HA]}',
        variables: ['pH', 'pKa'],
        description: 'pH of buffer solutions',
        condition: (params) => (params['analyteType'] ?? 1) === 2,
    },
    {
        id: 'weak_base_poh',
        name: 'Weak Base pOH',
        latex: 'pOH = pK_b + \\log\\frac{[B^+]}{[B]}',
        variables: ['pH'],
        description: 'pOH from Henderson-Hasselbalch for bases',
        condition: (params) => (params['analyteType'] ?? 1) === 4,
    },
    {
        id: 'equivalence_volume',
        name: 'Equivalence Point Volume',
        latex: 'V_{eq} = \\frac{C_a \\cdot V_a}{C_t}',
        variables: ['analyteConc', 'analyteVolume', 'titrantConc'],
        description: 'Volume of titrant needed to reach equivalence',
    },
    {
        id: 'moles_relationship',
        name: 'Moles at Equivalence',
        latex: 'n_{analyte} = n_{titrant}',
        variables: ['molesAnalyte', 'molesTitrant'],
        description: 'Equal moles at equivalence point',
    },
    {
        id: 'buffer_capacity',
        name: 'Buffer Capacity',
        latex: '\\beta = 2.303 \\cdot C \\cdot \\frac{K_a[H^+]}{(K_a + [H^+])^2}',
        variables: ['bufferCapacity'],
        description: 'Resistance to pH change',
        condition: (params) => (params['analyteType'] ?? 1) === 2,
    },
];
// ============================================================================
// FAILURE MODES
// ============================================================================
const FAILURE_MODES = [
    {
        id: 'past_equivalence',
        name: 'Past First Equivalence',
        description: 'Titration has passed the equivalence point',
        check: (state, params) => {
            const Va = params['analyteVolume'] ?? 50;
            const Ca = params['analyteConc'] ?? 0.1;
            const Ct = params['titrantConc'] ?? 0.1;
            const Veq = (Ca * Va) / Ct;
            return (state.variables['volumeAdded'] ?? 0) > Veq * 1.1;
        },
        severity: 'warning',
        educationalMessage: 'The titration has passed the equivalence point. ' +
            'In a real lab, the indicator color change would signal when to stop.',
    },
];
// ============================================================================
// EXPERIMENT CONFIG
// ============================================================================
export const TITRATION_CONFIG = {
    id: 'titration',
    slug: 'titration',
    name: 'Titration Curves',
    domain: 'chemistry',
    description: 'Explore acid-base titration and pH changes',
    longDescription: `
Titration is a technique to determine the concentration of an unknown solution
by adding a solution of known concentration until the reaction is complete.

Key features:
- Strong acid + strong base: steep curve at equivalence, pH 7
- Weak acid + strong base: buffer region, pH > 7 at equivalence
- Weak base + strong acid: buffer region, pH < 7 at equivalence

Important points:
- Equivalence point: moles of acid = moles of base
- Half-equivalence: pH = pKa (for weak acid)
- Buffer region: relatively flat part of curve
- Buffer capacity: resistance to pH change

Indicators:
- Different indicators change color at different pH ranges
- Choose indicator with color change near equivalence pH
  `.trim(),
    version: 1,
    conceptTags: ['titration', 'pH', 'acid-base', 'buffer', 'equivalence-point'],
    difficulty: 4,
    prerequisites: ['acids-bases', 'equilibrium'],
    parameters: PARAMETERS,
    equations: EQUATIONS,
    initialConditions: [
        { variableId: 'volumeAdded', type: 'fixed', value: 0 },
    ],
    failureModes: FAILURE_MODES,
    recommendedDt: 0.01,
    maxDt: 0.1,
};
// ============================================================================
// SIMULATION ENGINE
// ============================================================================
export class TitrationSimulation extends BaseSimulationEngine {
    config = TITRATION_CONFIG;
    constructor() {
        super({ volumeAdded: 0, pH: 7, totalVolume: 50, molesAnalyte: 0, molesTitrant: 0, bufferCapacity: 0, atEquivalence: 0 }, { type: 'euler', dt: 0.01, maxSubsteps: 10 });
    }
    /**
     * Calculate pH based on current state
     */
    calculatePH(volumeAdded, params) {
        const analyteType = params['analyteType'] ?? 1;
        const Ca = params['analyteConc'] ?? 0.1;
        const Va = params['analyteVolume'] ?? 50;
        const Ct = params['titrantConc'] ?? 0.1;
        const pKa = params['pKa'] ?? 4.76;
        const Kw = 1e-14; // Water dissociation constant
        const Ka = Math.pow(10, -pKa);
        // Initial moles
        const molesAnalyte0 = (Ca * Va) / 1000; // mL to L
        const molesTitrant = (Ct * volumeAdded) / 1000;
        const totalVolL = (Va + volumeAdded) / 1000;
        // Equivalence volume
        const Veq = (Ca * Va) / Ct;
        if (analyteType === 1) {
            // Strong acid titrated with strong base
            const molesH = molesAnalyte0 - molesTitrant;
            if (molesH > 1e-10) {
                // Before equivalence: excess acid
                const concH = molesH / totalVolL;
                return -Math.log10(concH);
            }
            else if (Math.abs(molesH) < 1e-10) {
                // At equivalence
                return 7;
            }
            else {
                // After equivalence: excess base
                const molesOH = molesTitrant - molesAnalyte0;
                const concOH = molesOH / totalVolL;
                const pOH = -Math.log10(concOH);
                return 14 - pOH;
            }
        }
        else if (analyteType === 2) {
            // Weak acid titrated with strong base
            const molesHA = molesAnalyte0 - molesTitrant;
            const molesA = molesTitrant;
            if (volumeAdded < 1e-6) {
                // Initial: just weak acid
                // HA ⇌ H⁺ + A⁻
                const concHA = Ca;
                const concH = Math.sqrt(Ka * concHA);
                return -Math.log10(concH);
            }
            else if (molesHA > 1e-10 && volumeAdded < Veq * 0.99) {
                // Buffer region: Henderson-Hasselbalch
                const ratio = molesA / molesHA;
                if (ratio > 0) {
                    return pKa + Math.log10(ratio);
                }
                return pKa;
            }
            else if (Math.abs(volumeAdded - Veq) < Veq * 0.02) {
                // At equivalence: conjugate base solution
                const concA = molesAnalyte0 / totalVolL;
                const Kb = Kw / Ka;
                const concOH = Math.sqrt(Kb * concA);
                const pOH = -Math.log10(concOH);
                return 14 - pOH;
            }
            else {
                // After equivalence: excess strong base
                const molesOH = molesTitrant - molesAnalyte0;
                const concOH = molesOH / totalVolL;
                const pOH = -Math.log10(concOH);
                return 14 - pOH;
            }
        }
        else if (analyteType === 3) {
            // Strong base titrated with strong acid
            const molesOH = molesAnalyte0 - molesTitrant;
            if (molesOH > 1e-10) {
                const concOH = molesOH / totalVolL;
                const pOH = -Math.log10(concOH);
                return 14 - pOH;
            }
            else if (Math.abs(molesOH) < 1e-10) {
                return 7;
            }
            else {
                const molesH = molesTitrant - molesAnalyte0;
                const concH = molesH / totalVolL;
                return -Math.log10(concH);
            }
        }
        else {
            // Weak base titrated with strong acid
            const Kb = Math.pow(10, -(14 - pKa));
            const molesB = molesAnalyte0 - molesTitrant;
            const molesBH = molesTitrant;
            if (volumeAdded < 1e-6) {
                const concB = Ca;
                const concOH = Math.sqrt(Kb * concB);
                const pOH = -Math.log10(concOH);
                return 14 - pOH;
            }
            else if (molesB > 1e-10 && volumeAdded < Veq * 0.99) {
                // Buffer region
                const ratio = molesB / molesBH;
                if (ratio > 0) {
                    const pOH = (14 - pKa) + Math.log10(ratio);
                    return 14 - pOH;
                }
                return 14 - (14 - pKa);
            }
            else if (Math.abs(volumeAdded - Veq) < Veq * 0.02) {
                // At equivalence: conjugate acid
                const concBH = molesAnalyte0 / totalVolL;
                const Ka_conj = Kw / Kb;
                const concH = Math.sqrt(Ka_conj * concBH);
                return -Math.log10(concH);
            }
            else {
                const molesH = molesTitrant - molesAnalyte0;
                const concH = molesH / totalVolL;
                return -Math.log10(concH);
            }
        }
    }
    getInitialState(params) {
        const Ca = params['analyteConc'] ?? 0.1;
        const Va = params['analyteVolume'] ?? 50;
        const pH = this.calculatePH(0, params);
        const molesAnalyte = (Ca * Va) / 1000;
        return {
            volumeAdded: 0,
            pH,
            totalVolume: Va,
            molesAnalyte,
            molesTitrant: 0,
            bufferCapacity: 0,
            atEquivalence: 0,
        };
    }
    computeDerivatives(_state, params) {
        const rate = params['additionRate'] ?? 0.5;
        return {
            volumeAdded: rate,
        };
    }
    integrate(dt) {
        const params = this.parameters;
        const rate = params['additionRate'] ?? 0.5;
        const Va = params['analyteVolume'] ?? 50;
        const Ca = params['analyteConc'] ?? 0.1;
        const Ct = params['titrantConc'] ?? 0.1;
        // Add titrant
        this.state.volumeAdded += rate * dt;
        // Calculate pH
        this.state.pH = this.calculatePH(this.state.volumeAdded, params);
        // Clamp pH to reasonable range
        this.state.pH = Math.max(0, Math.min(14, this.state.pH));
        // Update other state
        this.state.totalVolume = Va + this.state.volumeAdded;
        this.state.molesTitrant = (Ct * this.state.volumeAdded) / 1000;
        this.state.molesAnalyte = Math.max(0, (Ca * Va) / 1000 - this.state.molesTitrant);
        // Check if at equivalence
        const Veq = (Ca * Va) / Ct;
        this.state.atEquivalence = Math.abs(this.state.volumeAdded - Veq) < 0.5 ? 1 : 0;
    }
    getPositionForStabilityCheck() {
        return this.state.pH;
    }
    /**
     * Get equivalence point volume
     */
    getEquivalenceVolume() {
        const Ca = this.parameters['analyteConc'] ?? 0.1;
        const Va = this.parameters['analyteVolume'] ?? 50;
        const Ct = this.parameters['titrantConc'] ?? 0.1;
        return (Ca * Va) / Ct;
    }
    /**
     * Generate complete titration curve
     */
    generateTitrationCurve(maxVolume) {
        const Veq = this.getEquivalenceVolume();
        const max = maxVolume ?? Veq * 1.5;
        const points = [];
        for (let v = 0; v <= max; v += 0.5) {
            points.push({
                volume: v,
                pH: this.calculatePH(v, this.parameters),
            });
        }
        return points;
    }
}
/**
 * Factory function
 */
export function createTitration() {
    return new TitrationSimulation();
}
//# sourceMappingURL=titration.js.map